import UIUtils from 'js/lib/UIUtils';
import {
  assign,
  difference,
  filter,
  get,
  has,
  head,
  isEmpty,
  keys,
  map,
  reduce,
  split,
  values,
} from 'lodash';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import createFlowService from 'new/components/flows/service/createFlowService';
import { createResourceSetupService } from 'new/components/flows/types/replication/CreateReplicationFlow/ResourceSetup/service/createResourceSetupService';
import useFlows from 'new/components/flows/useFlows';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import usePaging from 'new/state/paging/repository/usePaging';
import { useCallback, useEffect } from 'react';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_VECTOR_DB,
} from 'js/constants/connectionCategories';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import Constants, { CONNECTORS_WITHOUT_PATH } from 'js/Constants';
import JSONUtils from 'js/lib/JSONUtils';
import { isUiModeCode } from 'new/utils/uiMode';

export default function useSinkSetup(formData, flowType) {
  const { getSinkConfigFields, getMetaInfoById } = usePaging();
  const {
    sinkConfig,
    selectedConnection,
    selectedBucket,
    isNewTable,
    tableNamesByDatabase,
    updateFormData,
    sink,
    nexset,
    selectedTable,
    credentialId,
  } = formData;
  const { getNexsetSchemaById } = useSchemas();
  const { onSinkMappingUpdated } = useFlows(updateFormData);
  const org = useCurrentOrg();

  const service = createResourceSetupService();
  const flowService = createFlowService();
  const databaseService = createDatabaseService();

  const sinkConfigFields = getSinkConfigFields(selectedConnection, flowType);
  const meta = getMetaInfoById(selectedConnection);
  const { connectionCategory, allowSinkAutoMapping } = meta;

  useEffect(handleMappingChange, [
    selectedTable,
    selectedTable?.columns?.length,
    sinkConfig?.mapping?.mode,
  ]);

  useEffect(() => {
    if (nexset?.id && !sink?.id) {
      const nexsetSchema = getNexsetSchemaById(nexset?.id);
      const sinkMapping = UIUtils.getSinkMappingFromAttributes(
        flowService.getSinkMappingAttributes({
          connectionCategory,
          sinkConfig,
          allAttributes: nexsetSchema,
        }),
      );

      const sinkConfigWithMapping = flowService.getSinkConfigWithMapping({
        sinkConfig,
        sinkMapping,
        connectionCategory,
        isEditMode: !!sink?.id,
      });

      updateFormData({ sinkMapping, sinkConfig: sinkConfigWithMapping });
    }
  }, [nexset?.id]);

  function handleMappingChange() {
    if (selectedTable) {
      const mode = get(sinkConfig, 'mapping.mode');
      const auto = (!mode && allowSinkAutoMapping) || mode === 'auto';
      const editedColumns = filter(selectedTable.columns, (c) => c.attr);
      const newMapping = reduce(
        editedColumns,
        (result, c) => {
          return {
            ...result,
            [c.attr]: { ...result[c.attr], [c.name]: c.type },
          };
        },
        {},
      );

      if (!isEmpty(newMapping)) {
        onSinkMappingUpdated({
          sinkConfig,
          isEditMode: !!sink?.id,
          connectionCategory,
        })(newMapping, auto);
      }
    }
  }

  const validate = (onError) => {
    if (!nexset) {
      onError('Nexset not detected yet. Try again in a few seconds');
      return false;
    }

    const isRest = connectionCategory === CONNECTION_CATEGORY_REST;
    const isTemplatizedAPI = connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API;
    const isVectorDb = connectionCategory === CONNECTION_CATEGORY_VECTOR_DB;

    // JSON/code mode: the user owns the full config in the Monaco editor, so
    // UI-field required checks, database/table selection, upsert key, and
    // mapping validations are intentionally skipped.  Only structural checks
    // that would produce a malformed payload (e.g. batch transform JSON) are
    // still enforced.
    if (isUiModeCode(sinkConfig)) {
      if (isRest || isTemplatizedAPI) {
        if (
          has(sinkConfig, 'batch.mode') &&
          !!sinkConfig['batch.mode'] &&
          has(sinkConfig, 'ui.batch_algo') &&
          sinkConfig['ui.batch_algo'] === Constants.GROUPING_ALGO[1].value &&
          has(sinkConfig, 'ui.body.transform.function') &&
          !!sinkConfig['ui.body.transform.function'] &&
          !JSONUtils.isJSON(sinkConfig['ui.body.transform.function'])
        ) {
          onError('Please enter a valid JSON');
          return false;
        }
      }
      return true;
    }

    if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      if (
        !databaseService.validateDatabase(
          {
            selectedConnection,
            selectedBucket,
            config: sinkConfig,
            configFields: sinkConfigFields,
            isNewTable,
            tables: tableNamesByDatabase,
            selectedTable,
            connectionType: 'SINK',
          },
          onError,
        )
      ) {
        return false;
      }
    }

    // Special validation for s3_iceberg connections
    // Special validation for file connectors without path
    if (
      CONNECTORS_WITHOUT_PATH.includes(selectedConnection) &&
      connectionCategory === CONNECTION_CATEGORY_FILE
    ) {
      //TODO: NEX-15904: this is a temporary fix to allow the creation of a sink without a path for file connectors. We need to refactor this to be more generic & to handle other scenarios.

      const missingFields = getEmptyRequiredFields(sinkConfigFields, sinkConfig);
      if (!isEmpty(missingFields)) {
        const errorMessage = missingFields.reduce(
          (message, field) => `${message} ${field.display_name},`,
          'Please enter the following required fields: ',
        );
        onError(errorMessage.substring(0, errorMessage.length - 1));
        return false;
      }
    }

    if (isRest || isTemplatizedAPI) {
      if (
        has(sinkConfig, 'batch.mode') &&
        !!sinkConfig['batch.mode'] &&
        has(sinkConfig, 'ui.batch_algo') &&
        sinkConfig['ui.batch_algo'] === Constants.GROUPING_ALGO[1].value && // Grouping algo ALGO_JSON check
        has(sinkConfig, 'ui.body.transform.function') &&
        !!sinkConfig['ui.body.transform.function'] &&
        !JSONUtils.isJSON(sinkConfig['ui.body.transform.function'])
      ) {
        onError('Please enter a valid JSON');
        return;
      }

      if (isRest && isEmpty(get(sinkConfig, 'url.template'))) {
        onError('Please enter an endpoint URL.');
        return;
      }
    }

    // FIX: Added connectionCategory === CONNECTION_CATEGORY_FILE check to prevent
    // folder validation from blocking non-file connectors (Dynamic Lookup, REST APIs, etc.).
    // The original logic used negative checks (isRest === false, etc.) which incorrectly
    // applied folder selection requirement to all non-REST/API/VectorDB connectors.
    // Kept all original checks for defense-in-depth validation.
    if (
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      isRest === false &&
      isTemplatizedAPI == false &&
      !isVectorDb &&
      !CONNECTORS_WITHOUT_PATH.includes(selectedConnection) &&
      (!selectedBucket || !credentialId)
    ) {
      onError('Please select a folder');
      return false;
    }

    if (sinkConfig && sinkConfig['insert.mode'] === 'UPSERT' && !sinkConfig['primary.key']) {
      onError('Please configure a primary key for this destination');
      return false;
    }

    let mappedFields = [];
    if (sinkConfig.mapping && sinkConfig.mapping.mapping) {
      mappedFields = map(values(sinkConfig.mapping.mapping), (value) => head(keys(value)));
    }
    let primaryKeys = split(sinkConfig['primary.key'], ',');
    let notMappedKeys = difference(primaryKeys, mappedFields);
    if (
      sinkConfig &&
      sinkConfig['insert.mode'] === 'UPSERT' &&
      sinkConfig['primary.key'] &&
      notMappedKeys.length > 1
    ) {
      onError('Primary key should be mapped to an attribute for this destination.');
      return false;
    }
    return true;
  };

  const setupInitialFormData = ({ sink, source, nexset }) => {
    const { selectedBucket } = service.getBucketInfoFromConfig(sink?.sink_config);

    let sinkConfig =
      sink?.sink_config ||
      flowService.createDefaultSinkConfig({
        selectedConnection,
        sinkConfig: sink?.sink_config,
        connectionCategory,
        allowSinkAutoMapping,
        dataSchema: null,
        orgId: org.id,
        configFields: sinkConfigFields,
      });

    const sinkCred = sink?.data_credentials ?? sink?.data_credentials_id;
    const resolvedCredentialId =
      typeof sinkCred === 'object' && sinkCred?.id != null
        ? sinkCred.id
        : (sinkCred ?? credentialId);

    updateFormData({
      source,
      sink,
      ...(nexset ? { nexset } : undefined),
      selectedBucket,
      credentialId: resolvedCredentialId,
      sinkConfig,
      selectedConnection: sink?.connector_type || selectedConnection,
    });
  };

  const onBucketSelected = useCallback(
    (selectedBucket) => {
      const path = selectedBucket?.path;
      const displayPath = selectedBucket && selectedBucket.displayPath;
      const mimeType = selectedBucket && selectedBucket.mimeType;
      const idPath = selectedBucket && selectedBucket.idPath; // For leaf-ID-only connectors (Google Drive)

      const sinkConfigWithBucket = assign({}, sinkConfig, {
        path,
        'ui.display_path': displayPath,
        'ui.selected_file_mime_type': mimeType,
        // Save the resolved ID path for leaf-ID-only connectors (Google Drive)
        // This allows future edits to skip iterative probing
        ...(idPath ? { 'ui.id_path': idPath } : {}),
      });
      // omit fields which shouldn't be rendered
      const visibleFieldData = UIUtils.reduceConfig(sinkConfigWithBucket, sinkConfigFields);
      const finalFieldData = UIUtils.configWithMissingDefaults(visibleFieldData, sinkConfigFields);
      updateFormData({
        sinkConfig: finalFieldData,
        selectedBucket,
      });
    },
    [sinkConfig, sinkConfigFields],
  );

  const onTableSelected = (selectedTable, isNewTable) => {
    let bucketObject = {
      table: null,
      database: null,
      mapping: null,
    };
    //TODO: Need to fix with Database name
    if (selectedTable) {
      const { columns, name, id, ...selectedTableConfig } = selectedTable;
      bucketObject = assign({}, bucketObject, selectedTableConfig);
      bucketObject.table = selectedTable?.name || null;

      let isEditMode = !!sink?.id;
      let previousTableName = '';
      // Original Value of Mode = 'addedDataSink.sink_config.mapping.mode'
      let previousMode = get(sinkConfig, 'mapping.mode', 'manual');
      if (isEditMode) {
        previousTableName = get(sink, 'sink_config.table');
      }
      let shouldOverrideMapping = false;

      // If Table change, then override mode value to manual
      // Don't override if it's a new table
      if (isEditMode && !!previousTableName && previousTableName !== name && !isNewTable) {
        shouldOverrideMapping = true;
      }
      // shouldOverrideMapping === false,
      // set mode value to initial edit mode value

      bucketObject.mapping = UIUtils.buildMapping(
        connectionCategory,
        sinkConfig.data_format,
        sinkConfig?.mapping?.mapping ||
          reduce(
            selectedTable.columns,
            (acc, c) => {
              if (c.attr) {
                return {
                  ...acc,
                  [c.attr]: { ...acc[c.attr], [c.name]: c.type },
                };
              }

              return acc;
            },
            {},
          ),
        sinkConfig.mapping,
        shouldOverrideMapping ? { mode: 'manual' } : { mode: previousMode },
      );
    }
    let sinkConfigWithBucket = assign({}, sinkConfig, bucketObject);

    if (allowSinkAutoMapping && sinkConfigWithBucket?.mapping?.mode !== 'manual') {
      sinkConfigWithBucket.mapping = { mode: 'auto' };
    }

    updateFormData({
      sinkConfig: sinkConfigWithBucket,
      selectedBucket: bucketObject,
      isNewTable,
      selectedTable,
      table: selectedTable,
    });
  };

  const cleanForm = (id) => {
    if (!!id) {
      updateFormData({
        credentialId: null,
        selectedCredentialInfo: null,
        connectionType: null,
        selectedConnection: null,
        selectedBucket: null,
        selectedTable: null,
        table: null,
        sourceConfig: null,
      });
    }
  };

  return {
    onBucketSelected,
    cleanForm,
    onTableSelected,
    validate,
    setupInitialFormData,
  };
}
